// Text measurement without a DOM.
//
// The scene layer runs in the browser, in vitest, and inside the exported HTML
// that headless Chrome turns into a PDF. Only one of those has a DOM, so label
// placement cannot depend on `measureText` or `getComputedTextLength`. Instead
// we carry Inter's own advance widths (see scripts/build-visual-font-metrics.mjs)
// and do the arithmetic ourselves.
//
// Every width here is a fraction of the em, so `width = Σadvance × fontSize`.

import { INTER_ADVANCES, INTER_FALLBACK_ADVANCE } from './interMetrics.generated'

export type FontWeight = 400 | 500 | 600 | 700

const WEIGHTS: FontWeight[] = [400, 500, 600, 700]

const nearestWeight = (weight: number): FontWeight => {
  let best: FontWeight = 400
  let bestDistance = Number.POSITIVE_INFINITY
  for (const candidate of WEIGHTS) {
    const distance = Math.abs(candidate - weight)
    if (distance < bestDistance) {
      bestDistance = distance
      best = candidate
    }
  }
  return best
}

/**
 * Widest digit advance for a weight. Used when the text is rendered with
 * `font-variant-numeric: tabular-nums`, where every digit occupies the same
 * box — measuring at the widest digit is then exact rather than approximate.
 */
const tabularDigitAdvance = (weight: FontWeight): number => {
  const table = INTER_ADVANCES[weight]
  let widest = 0
  for (const digit of '0123456789') {
    const advance = table[digit]
    if (advance != null && advance > widest) widest = advance
  }
  return widest || INTER_FALLBACK_ADVANCE[weight]
}

export type MeasureOptions = {
  size: number
  weight?: number
  /** Set when the text renders with tabular figures (all numeric labels do). */
  tabular?: boolean
}

/** Width of `text` in px at the given size and weight. */
export function measureText(text: string, options: MeasureOptions): number {
  const weight = nearestWeight(options.weight ?? 400)
  const table = INTER_ADVANCES[weight]
  const fallback = INTER_FALLBACK_ADVANCE[weight]
  const digitAdvance = options.tabular ? tabularDigitAdvance(weight) : null
  let em = 0
  for (const character of String(text ?? '')) {
    if (digitAdvance != null && character >= '0' && character <= '9') {
      em += digitAdvance
      continue
    }
    em += table[character] ?? fallback
  }
  return em * options.size
}

/**
 * Truncate to fit `maxWidth`, appending an ellipsis when anything was dropped.
 * Measures as it goes rather than guessing a character count — a fixed
 * character budget is not a width, and mixing the two is how labels overlap.
 * Returns the original string untouched when it already fits.
 */
export function truncateToWidth(text: string, maxWidth: number, options: MeasureOptions): string {
  const value = String(text ?? '')
  if (maxWidth <= 0) return ''
  if (measureText(value, options) <= maxWidth) return value

  const ellipsis = '…'
  const ellipsisWidth = measureText(ellipsis, options)
  if (ellipsisWidth > maxWidth) return ''

  const budget = maxWidth - ellipsisWidth
  let width = 0
  let kept = ''
  for (const character of value) {
    const advance = measureText(character, options)
    if (width + advance > budget) break
    width += advance
    kept += character
  }
  // Do not leave a dangling space or separator before the ellipsis.
  return `${kept.replace(/[\s,.\-–—/]+$/, '')}${ellipsis}`
}

/** Fits `text` into `maxWidth` by dropping size, never below `minSize`. */
export function fitFontSize(text: string, maxWidth: number, options: MeasureOptions, minSize = 8): number {
  let size = options.size
  while (size > minSize && measureText(text, { ...options, size }) > maxWidth) size -= 0.5
  return Math.max(minSize, size)
}
